/**
 * The controls a record form types into — `docs/record-flyouts.md` §3.
 *
 * Every one of them is `controlClass()` plus what it holds, which is the
 * point: a form built from these cannot have a 32px box beside a 36px one,
 * or a focus ring on one field and a border change on the next.
 *
 * What they replace is a page-local habit. `items-list.tsx` had `NumberField`,
 * `UnitSelect` and `Select`; `edit-equipment.tsx` had another `Select`;
 * `catalog-list.tsx` had `NumberField` and `DivisionPicker`. Five components
 * for two ideas, each with its own idea of what a number field looks like.
 */
import { type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { formatPhone } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { controlClass } from './field.js'
import { MenuSelect, type MenuOption } from '../menu-select.js'
import { DateField } from '../date-field.js'
import { Segmented } from '../primitives.js'
import { IconChevronDown, IconLock } from '../../frame/icons.js'

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'className'>

export interface TextInputProps extends NativeInput {
  id?: string
  describedBy?: string
  invalid?: boolean
  /** `big` is the 44px headline number; `touch` the 44px phone field. */
  size?: 'default' | 'big' | 'touch'
  /** 13px faint, before the value — the "$" on money. */
  prefix?: ReactNode
  /** 12px faint, after it — a unit, "verified", "best", "for SMS codes". */
  suffix?: ReactNode
  className?: string
}

/**
 * The plain one.
 *
 * The border and the ring live on the *wrapper*, not the input, because a
 * prefix and a suffix sit inside the box with the value — an input carrying
 * its own border inside a bordered span draws two rounded rectangles, which
 * is exactly the bug WP5 found in the quote line popup.
 */
export function TextInput({
  id, describedBy, invalid, prefix, suffix, size, className, disabled, readOnly, ...rest
}: TextInputProps) {
  return (
    <span className={controlClass({ invalid, disabled, readOnly, size, className })}>
      {prefix && <span className="shrink-0 text-[13px] text-text-faint">{prefix}</span>}
      <input
        {...rest}
        id={id}
        disabled={disabled}
        readOnly={readOnly}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className={cn('min-w-0 flex-1 border-none bg-transparent p-0 text-inherit',
          'font-inherit placeholder:text-text-faint focus:outline-none focus:ring-0',
          disabled && 'cursor-not-allowed')}
      />
      {suffix && (
        <span className="ml-auto shrink-0 whitespace-nowrap pl-2 text-[12px] text-text-muted">
          {suffix}
        </span>
      )}
    </span>
  )
}

/**
 * A number, right-aligned, with its unit inside the box.
 *
 * `inputMode="decimal"` rather than `type="number"`: the spinner arrows are
 * two tiny hit targets nobody uses that steal the scroll wheel, and a
 * `type="number"` input silently refuses "1,200" and reports an empty value
 * for anything it cannot parse — which is how a typed quantity becomes zero.
 *
 * `size="big"` is the 44px treatment for the one number a popup is about: a
 * quantity, a measure, a count of trips.
 */
export function NumberInput({
  unit, percent, size = 'default', className, ...rest
}: TextInputProps & { unit?: string; percent?: boolean }) {
  return (
    <TextInput
      {...rest}
      size={size}
      inputMode="decimal"
      suffix={percent ? '%' : unit}
      className={cn('[&_input]:text-right', className)}
    />
  )
}

/** A number with a dollar in front of it. Two decimals are the caller's to format. */
export function MoneyInput({ unit, className, ...rest }: TextInputProps & { unit?: string }) {
  return (
    <TextInput
      {...rest}
      inputMode="decimal"
      prefix="$"
      suffix={unit}
      className={cn('[&_input]:text-right', className)}
    />
  )
}

/**
 * A value the form is showing you rather than asking for.
 *
 * Text goes in a real `readOnly` input rather than a `<span>`, so the `Field`
 * label above it has something to name. A read-only field with an unattached
 * label reads to a screen reader as a label and then silence — the value it is
 * labelling is invisible — and the `Field` wrapper puts a `for` on every label
 * it draws, so the control below has to be labelable. A `ReactNode` value
 * (a chip, a tile, two lines) cannot live in an input, so that case keeps the
 * span and carries the id on it with an explicit role instead.
 *
 * It is never focused first: `Sheet`'s open-focus selector skips `[readonly]`,
 * so opening a form still lands on the first field somebody can actually type
 * in.
 */
export function ReadonlyInput({ value, className, id, describedBy }: {
  value: ReactNode
  className?: string
  id?: string
  describedBy?: string
}) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : null
  return (
    <span className={controlClass({ readOnly: true, className })}>
      {text === null
        ? (
          <span
            id={id}
            role="textbox"
            aria-readonly="true"
            aria-describedby={describedBy}
            className="min-w-0 flex-1 truncate"
          >
            {value}
          </span>
        )
        : (
          <input
            id={id}
            readOnly
            value={text}
            aria-describedby={describedBy}
            tabIndex={-1}
            className={cn('min-w-0 flex-1 truncate border-none bg-transparent p-0 text-inherit',
              'focus:outline-none focus:ring-0')}
          />
        )}
      <span aria-hidden className="shrink-0 text-text-faint"><IconLock size={13} /></span>
    </span>
  )
}

/**
 * A choice from a list — the app's menu, never a native `<select>`.
 *
 * `MenuSelect` already draws the right panel; what this adds is the 36px
 * control the rest of the form wears, so the dropdown beside a text field is
 * the same height and radius as the text field.
 */
export function SelectInput({
  value, onChange, options, placeholder = 'Choose…', label, id, describedBy, clearable,
  clearLabel, invalid, disabled, className, onCreate, createThing,
}: {
  value: string | null
  onChange: (value: string | null) => void
  options: MenuOption[]
  placeholder?: string
  label: string
  /** From `Field`, so the label above has something to point at. */
  id?: string
  describedBy?: string
  clearable?: boolean
  clearLabel?: string
  invalid?: boolean
  disabled?: boolean
  className?: string
  /** The menu's last row — "Create a new unit" — as `MenuSelect` offers it. */
  onCreate?: (query: string) => void
  createThing?: string
}) {
  return (
    <MenuSelect
      onCreate={onCreate}
      createThing={createThing}
      label={label}
      id={id}
      describedBy={describedBy}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      clearable={clearable}
      clearLabel={clearLabel}
      disabled={disabled}
      className={cn(controlClass({ invalid, disabled, className }),
        // MenuSelect draws its own chevron and its own open state.
        'justify-start font-semibold')}
      triggerContent={(chosen) => (
        <>
          <span className={cn('min-w-0 flex-1 truncate text-left',
            !chosen && 'font-medium text-text-faint')}>
            {chosen?.label ?? placeholder}
          </span>
          <span aria-hidden className="ml-auto shrink-0 pl-2 text-text-muted">
            <IconChevronDown size={12} />
          </span>
        </>
      )}
    />
  )
}

/**
 * A date, in the app's own masked field rather than the browser's.
 *
 * `<input type="date">` is a different control on every platform: a dropdown
 * on one, three spinners on another, a width that ignores the box it is in,
 * and a format that follows the operating system's locale rather than the
 * document's. `DateField` is 36px, prints "Sep 12, 2026", and is the same
 * everywhere.
 */
export function DateInput({ value, onChange, label, id, invalid, disabled, className }: {
  value: string
  onChange: (value: string) => void
  label: string
  /** From `Field`, so the label above has something to point at. */
  id?: string
  invalid?: boolean
  disabled?: boolean
  className?: string
}) {
  return (
    <DateField
      value={value}
      onChange={onChange}
      label={label}
      id={id}
      size="field"
      display="long"
      disabled={disabled}
      className={cn(invalid && 'border-danger', className)}
    />
  )
}

/**
 * Two or three exclusive choices, filling the field's width.
 *
 * For a question whose answers are short and whose shape matters — "Priced by:
 * company markup · its own markup · a fixed price" — where a dropdown would
 * hide two of the three answers behind a click.
 */
export function SegmentedField<T extends string>({
  options, value, onChange, label, labelledBy, describedBy,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  label: string
  /**
   * The id of the `Field` label naming this group — `Field`'s `labelId`, and
   * only from a `Field` marked `group`. A radiogroup is not labelable, so it
   * is named by pointing at the label rather than the other way round.
   */
  labelledBy?: string
  describedBy?: string
}) {
  return (
    <Segmented
      options={options}
      value={value}
      onChange={onChange}
      label={label}
      labelledBy={labelledBy}
      describedBy={describedBy}
      full
      size="field"
    />
  )
}

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  id?: string
  describedBy?: string
  invalid?: boolean
  className?: string
}

/** Prose: a note, a description, an access instruction. */
export function TextArea({
  id, describedBy, invalid, rows = 3, className, disabled, ...rest
}: TextAreaProps) {
  return (
    <textarea
      {...rest}
      id={id}
      rows={rows}
      disabled={disabled}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={cn(controlClass({ invalid, disabled }),
        'h-auto resize-y py-[9px] px-[11px] font-medium leading-[1.45]',
        className)}
    />
  )
}

/**
 * A phone number that formats itself as it is typed.
 *
 * `(717) 555-0142`, and `(717) 555-0142 x204` once there are digits past the
 * tenth. Letters and punctuation never reach the value — they are dropped on
 * the way in, so a pasted "Tel: 717.555.0142 ext 204" lands correctly instead
 * of being stored as a sentence.
 *
 * It reports the formatted text, not the digits: that is what the box shows,
 * what the column holds today, and what a person reads back on a visit.
 * `normalizePhone` in `@rowted/shared` is there for matching two records of
 * the same person.
 */
export function PhoneInput({ value, onChange, ...rest }: Omit<TextInputProps, 'onChange'> & {
  value: string
  onChange: (next: string) => void
}) {
  return (
    <TextInput
      {...rest}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={formatPhone(value)}
      onChange={(event) => onChange(formatPhone(event.target.value))}
    />
  )
}
